import { createAsyncThunk } from '@reduxjs/toolkit';
import { AxiosError } from 'axios';
import {
  findBookings,
  FindBookingsParams,
  FindBookingsResponse,
} from 'src/api/bookingsView';
import { SortDirection } from 'src/api/types';
import { PlaceType } from 'src/types';
import { defaultServerError } from 'src/utils/consts';
import { getDateTime } from 'src/utils/getDateTime';

import { messagesActions } from '../messages/slice';
import { StoreState, ThunkConfig } from '../types';

import { emptyBookings } from './initialState';
import { getSearchState } from './selectors';

const defaultLimit = 15;

const datesToFindBookingsParams = (
  from: string | undefined,
  to: string | undefined,
): Partial<FindBookingsParams> => {
  if (!from || !to) return {};

  const fromDate = from
    ? getDateTime({
        date: new Date(from),
        hours: 0,
        minutes: 0,
        seconds: 0,
        milliseconds: 0,
      })
    : undefined;
  const toDate = to
    ? getDateTime({
        date: new Date(to),
        hours: 23,
        minutes: 59,
        seconds: 59,
        milliseconds: 999,
      })
    : undefined;
  if (toDate) toDate.setDate(toDate.getDate() + 1);

  return {
    searchFrom: fromDate ? fromDate.toISOString() : undefined,
    searchTo: toDate ? toDate.toISOString() : undefined,
  };
};

export interface SearchBookingsReturn extends FindBookingsResponse {
  now: Date;
}

export const searchBookings = createAsyncThunk<
  SearchBookingsReturn | void,
  boolean,
  ThunkConfig<string>
>(
  'search/searchBookings',
  async (fromStart, { dispatch, getState, rejectWithValue, requestId }) => {
    try {
      const state = getState() as StoreState;
      const {
        from,
        to,
        token,
        hasMore: hasMoreFromState,
        now: nowFromState,
        currentRequestId,
        employee,
      } = getSearchState(state);

      const now = fromStart ? new Date() : nowFromState;
      const hasMore = fromStart ? true : hasMoreFromState;

      if (!hasMore || !now || currentRequestId !== requestId) return;

      if (!employee)
        return {
          now,
          bookings: emptyBookings,
          nextPageToken: null,
        };

      const params: FindBookingsParams = {
        limit: defaultLimit,
        deleted: false,
        filterEmployeeNumber: employee.id as string,
        filterPlaceType: PlaceType.Desk,
        bookedToAfter: now.toISOString(),
        sortDirection: SortDirection.Asc,
        sortField: 'dateBookingFrom',
        ...datesToFindBookingsParams(from, to),
      };

      if (fromStart) {
        params.offset = 0;
      } else if (token) {
        params.from = token;
      }

      const response = await findBookings(params);

      return { ...response, now };
    } catch (e) {
      dispatch(messagesActions.show({ message: defaultServerError }));
      return rejectWithValue((e as AxiosError).message);
    }
  },
);
